import { CheckOutlined } from '@mui/icons-material'
import {
  CircularProgress,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material'
import { t } from 'i18next'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { makeStyles } from 'tss-react/mui'
import { Key } from 'utils/keyCodes'

type Props = {
  loading: boolean
  saveOnEnterFunction?: (value: string) => void
  onBlurFunction?: (value: string) => void
  loadingVariant?: 'helperText' | 'endAdornment'
  /**
   * if true the text field will not register key down events
   * util if changing another field that the current text field depends on
   */
  disableChanges?: boolean
} & TextFieldProps

const useStyles = makeStyles({ name: '' })((theme) => ({
  savedIcon: {
    opacity: 1,
    transition: 'opacity .5s ease',
  },
  hiddenSavedIcon: {
    opacity: '0 !important',
  },
}))

const AdornmentWrapper = ({ children }: { children?: React.ReactNode }) => (
  <div
    style={{
      minWidth: '28px',
      minHeight: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
    }}
  >
    {children}
  </div>
)

export const TextFieldWithLoading = (props: Props) => {
  const { classes } = useStyles()
  const {
    loading,
    saveOnEnterFunction,
    onBlurFunction,
    disableChanges = false,
    loadingVariant = 'helperText',
    onChange,
    slotProps,
    ...textFieldProps
  } = props

  const handleSaveOnEnter = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
  ) => {
    if (disableChanges) {
      e.preventDefault()
      return false
    }
    if (!saveOnEnterFunction) {
      return true
    }

    if (
      (!props?.multiline && e.key === Key.Enter) ||
      (props?.multiline && e.key === Key.Enter && (e.ctrlKey || e.metaKey))
    ) {
      props.saveOnEnterFunction(
        e.currentTarget?.['value'] || e.target?.['value'],
      )
    }

    return true
  }

  const mounted = useRef(false)
  const hadFocusBeforeLoading = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [LoadingOrSaveIcon, setLoadingOrSaveIcon] = useState<React.ReactNode>(
    <AdornmentWrapper>
      {props.slotProps?.input?.['endAdornment']}
    </AdornmentWrapper>,
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (
        e.currentTarget.value !==
        (textFieldProps.value ?? textFieldProps.defaultValue)
      ) {
        if (onChange) {
          onChange(e)
        }
      }
    },
    [textFieldProps.value, textFieldProps.defaultValue, onChange],
  )

  useEffect(() => {
    if (loading === undefined || !mounted.current) {
      return
    }

    let loadingOrSaveIcon: React.ReactNode = null

    if (loading === false) {
      const iconId = crypto.randomUUID()
      loadingOrSaveIcon = (
        <Tooltip title={t('common:saved', 'saved')}>
          <CheckOutlined
            color="success"
            className={classes.savedIcon}
            id={iconId}
            fontSize="small"
          />
        </Tooltip>
      )
      setTimeout(() => {
        if (mounted.current) {
          document
            .getElementById(iconId)
            ?.classList.add(classes.hiddenSavedIcon)
          setTimeout(() => {
            if (hadFocusBeforeLoading.current && inputRef.current) {
              inputRef.current.focus()
              hadFocusBeforeLoading.current = false
            }
            setLoadingOrSaveIcon(
              <AdornmentWrapper>
                {slotProps?.input?.['endAdornment']}
              </AdornmentWrapper>,
            )
          }, 500)
        }
      }, 500)
    } else if (loading === true) {
      // Capture if TextField had focus before loading started
      hadFocusBeforeLoading.current =
        inputRef.current === document.activeElement

      loadingOrSaveIcon = (
        <Tooltip title={t('common:saving', 'saving')}>
          <span>
            <CircularProgress
              size={'12px'}
              style={{ width: '12px', height: '12px' }}
            />
          </span>
        </Tooltip>
      )
    }

    setLoadingOrSaveIcon(
      <AdornmentWrapper>{loadingOrSaveIcon}</AdornmentWrapper>,
    )
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classes.hiddenSavedIcon,
    classes.savedIcon,
    loading,
    setLoadingOrSaveIcon,
  ])

  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  return (
    <TextField
      {...textFieldProps}
      inputRef={inputRef}
      onChange={handleChange}
      onDoubleClick={(e) => {
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        if (e.key === Key.Escape) {
          lastKey.current = Key.Escape
        }

        if (props.onKeyDown) {
          props.onKeyDown(e)
          return
        } else if (handleSaveOnEnter) {
          handleSaveOnEnter(e)
          return
        }
      }}
      helperText={
        loadingVariant === 'helperText' && !props.helperText ? (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ flex: '1 1 100%' }}>
              {textFieldProps.helperText}
            </span>
            {LoadingOrSaveIcon}
          </span>
        ) : (
          props.helperText
        )
      }
      onBlur={(e) => {
        return props.onBlur
          ? props.onBlur(e)
          : props.onBlurFunction?.(e.currentTarget.value)
      }}
      onFocus={(e) => e.currentTarget.select()}
      onDrop={(e) => {
        e.preventDefault()
        const droppedValue = e.dataTransfer.getData('text/plain')
        if (droppedValue) {
          props.onChange?.({
            ...e,
            currentTarget: { ...e.currentTarget, value: droppedValue },
          } as any)

          if (saveOnEnterFunction) {
            saveOnEnterFunction(droppedValue)
          } else if (props.onKeyDown) {
            props.onKeyDown({
              ...e,
              currentTarget: { ...e.currentTarget, value: droppedValue },
              key: Key.Enter,
            } as any)
          } else if (props.onBlurFunction) {
            props.onBlurFunction(droppedValue)
          }
        }
      }}
      slotProps={{
        input: {
          ...(slotProps?.input || {}),
          endAdornment:
            loadingVariant === 'endAdornment' &&
            (LoadingOrSaveIcon ?? slotProps?.input?.['endAdornment']),
        },
        htmlInput: {
          ...(slotProps?.htmlInput || {}),
          style: {
            ...(slotProps?.htmlInput?.['style'] || {}),
            paddingRight: '12px',
          },
        },
      }}
    />
  )
}
